import { useEffect, useState } from "react";
import { isPhone } from "@/mobile/device";

// Выбор микрофона и аудиовыхода для софтфона. Вынесено из CallModal как есть:
// им пользуются и обычный исходящий звонок, и окно входящего (прослушка →
// перехват) — выбор между ними общий, через localStorage.

export interface AudioDevice {
  deviceId: string;
  label: string;
}

export const MIC_KEY = "newats.call.micId";
export const SPK_KEY = "newats.call.speakerId";

/** Телефон: выбирать нечего — микрофон и выход системные. Выход там решает
 *  сама ОС (динамик, наушники, AirPods), а iOS и не даёт его выбрать. */
export function systemAudioOnly(): boolean {
  return typeof window !== "undefined" && isPhone();
}

/** Последний выбор рекрутёра — без перечисления устройств. Нужен там, где
 * звонок начинается раньше, чем окно успевает получить список (окно входящего
 * запускает прослушку прямо из клика). Отключённое с тех пор устройство
 * софтфон переживёт сам: микрофон откатится на системный, выход — на "". */
export function savedAudioDevices(): { micId: string; speakerId: string } {
  if (systemAudioOnly()) return { micId: "", speakerId: "" };
  try {
    return {
      micId: localStorage.getItem(MIC_KEY) ?? "",
      speakerId: localStorage.getItem(SPK_KEY) ?? "",
    };
  } catch {
    return { micId: "", speakerId: "" };
  }
}

export function useAudioDevices() {
  const [mics, setMics] = useState<AudioDevice[] | null>(null);
  const [speakers, setSpeakers] = useState<AudioDevice[]>([]);
  const [micId, setMicId] = useState("");
  const [speakerId, setSpeakerId] = useState("");
  const [permError, setPermError] = useState<string | null>(null);
  const [systemOnly] = useState(systemAudioOnly);
  const supportsSinkId = typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId === "function";

  // Enumerate audio devices (labels require a granted mic permission first).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        // Телефон: разрешение на микрофон — всё, что тут было нужно. Списка
        // нет, выбор пустой («по умолчанию»).
        if (systemOnly) {
          if (alive) setMics([]);
          return;
        }
        const devs = await navigator.mediaDevices.enumerateDevices();
        if (!alive) return;
        const ins = devs
          .filter((d) => d.kind === "audioinput")
          .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Микрофон ${i + 1}` }));
        const outs = devs
          .filter((d) => d.kind === "audiooutput")
          .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Динамик ${i + 1}` }));
        setMics(ins);
        setSpeakers(outs);
        // Reuse the last picked device if it's still plugged in, else default
        // to the first one.
        const savedMic = localStorage.getItem(MIC_KEY);
        const savedSpk = localStorage.getItem(SPK_KEY);
        setMicId(ins.find((d) => d.deviceId === savedMic)?.deviceId ?? ins[0]?.deviceId ?? "");
        // Default output = the system default ("" sink), not the first
        // enumerated device — forcing outs[0] can route audio to a device the
        // recruiter isn't listening on ("I don't hear the candidate"). Only a
        // previously, explicitly chosen device overrides it.
        setSpeakerId(outs.find((d) => d.deviceId === savedSpk)?.deviceId ?? "");
      } catch (e) {
        if (alive) {
          setMics([]);
          setPermError(
            e instanceof DOMException && e.name === "NotAllowedError"
              ? "Нет доступа к микрофону — разрешите его в браузере."
              : systemOnly
                ? "Не удалось включить микрофон."
                : "Не удалось получить список устройств.",
          );
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [systemOnly]);

  // Persist the chosen devices so the next call preselects them.
  useEffect(() => {
    if (micId && !systemOnly) localStorage.setItem(MIC_KEY, micId);
  }, [micId, systemOnly]);
  useEffect(() => {
    if (speakerId && !systemOnly) localStorage.setItem(SPK_KEY, speakerId);
  }, [speakerId, systemOnly]);

  return {
    mics,
    speakers,
    micId,
    setMicId,
    speakerId,
    setSpeakerId,
    permError,
    supportsSinkId,
    /** Телефон: списков не показывать — всё системное. */
    systemOnly,
  };
}
